import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import { toast } from 'sonner';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  User, 
  Mail, 
  Calendar,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';

export default function ExtensionRequestsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const fetchRequests = async () => {
    try {
      const response = await api.get('/admin/extension-requests');
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to load extension requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchRequests, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleApprove = async (userId) => {
    if (!window.confirm('Are you sure you want to approve this extension request?')) {
      return;
    }

    try {
      setProcessing(userId);
      await api.post(`/admin/extension-requests/${userId}/approve`);
      toast.success('Extension approved! User has been notified via email.');
      await fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (userId) => {
    const reason = window.prompt(
      'Optional: Provide a reason for rejection (will be sent to the user):'
    );
    
    if (reason === null) return; // User cancelled

    try {
      setProcessing(userId);
      await api.post(`/admin/extension-requests/${userId}/reject`, null, {
        params: { reason: reason || undefined }
      });
      toast.success('Extension rejected. User has been notified via email.');
      await fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500 dark:text-gray-400">Loading requests...</div>
      </div>
    );
  }

  if (requests.length === 0) {
    return (
      <div className="text-center py-12">
        <Clock className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
          No Pending Extension Requests
        </h3>
        <p className="text-gray-500 dark:text-gray-400">
          Extension requests will appear here when users submit them.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Trial Extension Requests
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {requests.length} pending request{requests.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={fetchRequests}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Requests List */}
      <div className="space-y-4">
        {requests.map((request) => {
          const isUrgent = request.hours_until_auto_reject < 6;
          const isCritical = request.hours_until_auto_reject < 1;

          return (
            <div
              key={request.id}
              className={`bg-white dark:bg-gray-800 rounded-xl border-2 ${
                isCritical
                  ? 'border-red-500'
                  : isUrgent
                  ? 'border-amber-500'
                  : 'border-gray-200 dark:border-gray-700'
              } shadow-sm p-6`}
            >
              {/* Time Warning Banner */}
              {(isUrgent || isCritical) && (
                <div
                  className={`mb-4 ${
                    isCritical
                      ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                      : 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800'
                  } rounded-lg px-4 py-3`}
                >
                  <div className="flex items-center gap-2">
                    <AlertTriangle
                      className={`w-5 h-5 ${
                        isCritical ? 'text-red-600' : 'text-amber-600'
                      }`}
                    />
                    <span
                      className={`font-semibold ${
                        isCritical
                          ? 'text-red-900 dark:text-red-100'
                          : 'text-amber-900 dark:text-amber-100'
                      }`}
                    >
                      {isCritical
                        ? 'URGENT: Less than 1 hour until auto-reject!'
                        : `${Math.floor(request.hours_until_auto_reject)} hours until auto-reject`}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                {/* User Info */}
                <div className="flex-1 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {request.full_name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                        {request.full_name || 'Unknown User'}
                      </h3>
                      <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 text-sm">
                        <Mail className="w-4 h-4" />
                        {request.email}
                      </div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <Calendar className="w-4 h-4" />
                      <span>
                        Trial Started: {formatDate(request.trial_start_date)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <Calendar className="w-4 h-4" />
                      <span>
                        Trial Ends: {formatDate(request.trial_end_date)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <Clock className="w-4 h-4" />
                      <span>
                        Requested: {formatDate(request.extension_request_date)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 font-medium text-blue-600 dark:text-blue-400">
                      <CheckCircle className="w-4 h-4" />
                      <span>Extension: 5 additional days</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex lg:flex-col gap-3 lg:items-end">
                  <button
                    onClick={() => handleApprove(request.id)}
                    disabled={processing === request.id}
                    className="flex-1 lg:flex-none flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    <CheckCircle className="w-5 h-5" />
                    {processing === request.id ? 'Processing...' : 'Approve'}
                  </button>
                  <button
                    onClick={() => handleReject(request.id)}
                    disabled={processing === request.id}
                    className="flex-1 lg:flex-none flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    <XCircle className="w-5 h-5" />
                    {processing === request.id ? 'Processing...' : 'Reject'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info Box */}
      <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
        <div className="flex gap-3">
          <AlertTriangle className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
              Important Notes
            </h4>
            <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-disc list-inside">
              <li>Extension requests are automatically rejected after 24 hours if no action is taken</li>
              <li>Approved extensions grant 5 additional days of trial access</li>
              <li>Users receive email notifications for all actions</li>
              <li>Extension can only be requested once per user</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
